import { zodResolver } from '@hookform/resolvers/zod';
import CT from '@marketplace-ai/tags';
import { ReactNode, useEffect, useRef } from 'react';
import { FormProvider, Path } from 'react-hook-form';
import { FormLayout, FormRow as Row, FormSection as Section } from '~/components';
import { Wizard } from '~/components/Wizard/Wizard';
import { WizardStepsProps } from '~/components/Wizard/types';
import { useCustomForm } from '~/hooks/useCustomForm';
import { useServerWarnings } from '~/hooks/useServerWarnings';
import { useWizardSteps } from '~/hooks/useWizardSteps';
import { CreatableSelect, DatePicker, Select, TextArea, TextInput } from '../../../components';
import { COUNTRIES, STATES } from '../../../constants';
import { STATUS_OPTIONS } from './constants';
import {
  CreateMedLicenseFormValues,
  isInactiveFieldRequired,
  isNonPractisingLabel,
  medicalLicenseSchema,
  MedicalTypeOptions,
  MedLicenseFormValues,
} from './types';

const TAGS = CT.medicalLicenses.form;
const VALID_FIELDS = [
  'origIssueDate',
  'renewalDate',
  'expiryDate',
  'currStatus',
  'inactiveDate',
] as (keyof MedLicenseFormValues)[];

const STEP_FIELDS: Path<MedLicenseFormValues>[][] = [
  ['licenseType', 'licenseNumber', 'countryCode', 'stateCode'],
  ['currStatus'],
  ['origIssueDate', 'renewalDate', 'expiryDate', 'inactiveDate', 'inactiveExplainTx'],
];

const validateMedicalFieldsOnStepEnter = (
  step: number,
  values: MedLicenseFormValues
): Path<MedLicenseFormValues>[] => {
  if (step !== 2) return [];

  // Status is selected on step two, while the fields that can correct its
  // cross-field conflicts live on the Dates step. Revalidate only values the
  // user already has; validating blank future fields here would show required
  // errors before the user has had a chance to fill them in.
  const fields: Path<MedLicenseFormValues>[] = [];
  if (values.expiryDate) fields.push('expiryDate');
  if (values.inactiveDate) fields.push('inactiveDate');
  return fields;
};

interface FormProps {
  medLicensesForm: Partial<MedLicenseFormValues>;
  create: boolean;
  _medicalLicenseTypeOptions: MedicalTypeOptions[];
  addLicense: (license: CreateMedLicenseFormValues) => void;
  editLicense: (license: MedLicenseFormValues) => void;
  handleClose: () => void;
}

export const MedicalLicenseForm = (props: FormProps) => {
  const { addLicense, editLicense, medLicensesForm, create, _medicalLicenseTypeOptions } = props;

  const methods = useCustomForm<MedLicenseFormValues>({
    resolver: zodResolver(medicalLicenseSchema),
    defaultValues: {
      ...medLicensesForm,
      // Snapshot the cross-field values as loaded so existing imported
      // conflicts do not block unrelated edits. New or changed conflicts are
      // still rejected by the schema.
      originalCurrStatus: medLicensesForm.currStatus?.label ?? '',
      originalExpiryDate: medLicensesForm.expiryDate ?? '',
      originalInactiveDate: medLicensesForm.inactiveDate ?? '',
      originalInactiveExplainTx: medLicensesForm.inactiveExplainTx ?? '',
    },
  });
  const { handleSubmit, watch, setValue, getValues } = methods;

  const onSubmitSuccess = (values: MedLicenseFormValues | CreateMedLicenseFormValues) => {
    console.log('Med License Submit Success: ', values);
    if (create) {
      addLicense(values as CreateMedLicenseFormValues);
    } else {
      editLicense(values as MedLicenseFormValues);
    }
  };

  const onSubmitError = (err: unknown) => console.log('Med License Submit Error: ', err);
  // [MCV-1574] Whether the inactive fields show/are required is now driven by
  // currStatus rather than the old manual "Is the License Inactive?" toggle.
  const currStatusLabel = watch('currStatus').label;
  const isNonPractising = isNonPractisingLabel(currStatusLabel);
  // [MCV-1718] Same predicate and same mount-time snapshot the schema uses,
  // so the required markers never claim more than validation enforces. The
  // `medLicensesForm` prop is not read here: the machine resets it to the
  // empty form while the dialog is still fading out.
  const originalStatusLabel = getValues('originalCurrStatus');
  const inactiveDateRequired = isInactiveFieldRequired(
    currStatusLabel,
    originalStatusLabel,
    getValues('originalInactiveDate')
  );
  const inactiveExplainRequired = isInactiveFieldRequired(
    currStatusLabel,
    originalStatusLabel,
    getValues('originalInactiveExplainTx')
  );
  const countryCodeCheck = watch('countryCode').value === 'US';
  const { currentStep, goBack, goNext, navigationInProgress } = useWizardSteps({
    methods,
    stepFields: STEP_FIELDS,
    validateFieldsOnStepEnter: validateMedicalFieldsOnStepEnter,
  });

  // [MCV-1574] Track the previous render's membership so the clear below only
  // fires on a genuine in-session transition, never on mount.
  const wasNonPractisingRef = useRef(isNonPractising);

  useEffect(() => {
    // Clear the inactive fields only when the status actively LEAVES the
    // non-practising set during this session (e.g. Expired -> Active). The
    // API 400s "An active license cannot have an inactive date", and with the
    // toggle gone there is no other way for the user to see/clear a stale
    // inactiveDate, so this prevents that dead end.
    //
    // Deliberately does NOT fire just because the row loads already
    // mismatched (e.g. a pre-MCV-594 Active row with a populated
    // inactiveDate) -- clearing on mount would silently destroy that legacy
    // value before the user ever sees the form. The schema's snapshot-based
    // grandfather rule (medicalLicenseSchema) is what lets an untouched
    // legacy conflict like that save instead.
    if (wasNonPractisingRef.current && !isNonPractising) {
      setValue('inactiveDate', '');
      setValue('inactiveExplainTx', '');
    }
    wasNonPractisingRef.current = isNonPractising;
  }, [isNonPractising]);

  useServerWarnings(methods, medLicensesForm.warnings, VALID_FIELDS);

  const wizardSteps: WizardStepsProps[] = [
    {
      header: 'License Type & Jurisdiction',
      label: 'License',
      cyTag: `${TAGS.form}-license-step`,
      footer: {
        secondaryButton: {
          label: 'Close',
          cyTag: TAGS.closeBtn,
          onClick: props.handleClose,
        },
        primaryButton: {
          label: 'Next',
          cyTag: `${TAGS.submitBtn}-next-license`,
          disabled: navigationInProgress,
          onClick: goNext,
        },
      },
      children: ({ footer }: { footer: ReactNode }) => (
        <>
          <Section componentsPerLine={2}>
            <Row>
              <CreatableSelect
                options={_medicalLicenseTypeOptions}
                name={'licenseType'}
                label={'License Type'}
                cyTag={TAGS.typeSelect}
                required
              />
            </Row>
            <Row>
              <TextInput
                label={'License Number'}
                name={'licenseNumber'}
                cyTag={TAGS.numberInput}
                required
              />
            </Row>
            <Row>
              <Select
                label={'Country'}
                name={'countryCode'}
                cyTag={TAGS.countrySelect}
                options={COUNTRIES}
                required
              />
            </Row>
            {countryCodeCheck && (
              <Row>
                <Select
                  label={'State'}
                  name={'stateCode'}
                  cyTag={TAGS.stateSelect}
                  options={STATES}
                  required
                />
              </Row>
            )}
          </Section>
          {footer}
        </>
      ),
    },
    {
      header: 'Status',
      label: 'Status',
      cyTag: `${TAGS.form}-status-step`,
      footer: {
        backButton: {
          label: 'Back',
          cyTag: `${TAGS.submitBtn}-back-status`,
          onClick: goBack,
        },
        secondaryButton: {
          label: 'Close',
          cyTag: TAGS.closeBtn,
          onClick: props.handleClose,
        },
        primaryButton: {
          label: 'Next',
          cyTag: `${TAGS.submitBtn}-next-status`,
          disabled: navigationInProgress,
          onClick: goNext,
        },
      },
      children: ({ footer }: { footer: ReactNode }) => (
        <>
          <Section componentsPerLine={1}>
            <Row>
              <Select
                cyTag={TAGS.statusSelect}
                label={'Current Status'}
                name={'currStatus'}
                options={STATUS_OPTIONS}
                required
              />
            </Row>
          </Section>
          {footer}
        </>
      ),
    },
    {
      header: 'Dates',
      label: 'Dates',
      cyTag: `${TAGS.form}-dates-step`,
      footer: {
        backButton: {
          label: 'Back',
          cyTag: `${TAGS.submitBtn}-back-dates`,
          onClick: goBack,
        },
        secondaryButton: {
          label: 'Close',
          cyTag: TAGS.closeBtn,
          onClick: props.handleClose,
        },
        primaryButton: {
          label: 'Save',
          cyTag: TAGS.submitBtn,
          isSubmit: true,
        },
      },
      children: ({ footer }: { footer: ReactNode }) => (
        <>
          <Section componentsPerLine={2}>
            <Row>
              <DatePicker
                label={'Original Issue Date'}
                cyTag={TAGS.issueDatePicker}
                name={'origIssueDate'}
                required
              />
            </Row>
            <Row>
              <DatePicker
                label={'Renewal Date'}
                cyTag={TAGS.renewalDatePicker}
                name={'renewalDate'}
                required
              />
            </Row>
            <Row>
              <DatePicker
                label={'Expiration Date'}
                cyTag={TAGS.expirationDatePicker}
                name={'expiryDate'}
                required
              />
            </Row>
          </Section>
          {isNonPractising && (
            <Section componentsPerLine={1}>
              <Row>
                <DatePicker
                  label={'Inactive Date'}
                  cyTag={TAGS.inactiveDatePicker}
                  name={'inactiveDate'}
                  required={inactiveDateRequired}
                />
              </Row>
              <Row>
                <TextArea
                  label={'Explanation'}
                  description={'Explain why the license is inactive'}
                  cyTag={TAGS.inactiveExplanation}
                  name={'inactiveExplainTx'}
                  required={inactiveExplainRequired}
                />
              </Row>
            </Section>
          )}
          {footer}
        </>
      ),
    },
  ];

  return (
    <FormProvider {...methods}>
      <form
        data-cy={TAGS.form}
        onSubmit={handleSubmit(onSubmitSuccess, onSubmitError)}
        id="Medical License Form"
      >
        <FormLayout>
          <Wizard steps={wizardSteps} currentStep={currentStep} />
        </FormLayout>
      </form>
    </FormProvider>
  );
};
